import { useState, useEffect, useCallback } from "react";
import { AgentLayout } from "./AgentDashboard";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { RefreshCw, CheckCircle2, XCircle, AlertTriangle, Clock, Activity, Zap, Server, Database, TrendingUp } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  healthy: { color: "text-emerald-500", bg: "bg-emerald-500/10", border: "border-emerald-500/30", icon: CheckCircle2, label: "Healthy" },
  degraded: { color: "text-amber-500", bg: "bg-amber-500/10", border: "border-amber-500/30", icon: AlertTriangle, label: "Degraded" },
  unhealthy: { color: "text-red-500", bg: "bg-red-500/10", border: "border-red-500/30", icon: XCircle, label: "Unhealthy" },
};

function StatusBadge({ status }) {
  const cfg = statusConfig[status] || statusConfig.unhealthy;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.color} border ${cfg.border}`}>
      <Icon className="h-3.5 w-3.5" />
      {cfg.label}
    </span>
  );
}

function ResponseBar({ value, max = 100 }) {
  const pct = Math.min((value / max) * 100, 100);
  const color = value < 20 ? "bg-emerald-500" : value < 50 ? "bg-amber-500" : "bg-red-500";
  return (
    <div className="flex items-center gap-2 w-full">
      <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-mono text-muted-foreground w-16 text-right">{value.toFixed(1)}ms</span>
    </div>
  );
}

export default function SystemHealth() {
  const [latestCheck, setLatestCheck] = useState(null);
  const [history, setHistory] = useState([]);
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const token = localStorage.getItem("agent_token");

  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const runHealthCheck = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/agent/health-check`, { headers });
      if (!res.ok) throw new Error("Health check failed");
      const data = await res.json();
      setLatestCheck(data);
      toast.success(`Health check complete: ${data.summary.health_percentage}% healthy`);
      fetchHistory();
      fetchTrends();
    } catch (e) {
      toast.error("Failed to run health check: " + e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchHistory = async () => {
    try {
      const res = await fetch(`${API}/api/agent/health-check/history?limit=20`, { headers });
      if (res.ok) {
        const data = await res.json();
        setHistory(data.checks || []);
      }
    } catch {}
  };

  const fetchTrends = async () => {
    try {
      const res = await fetch(`${API}/api/agent/health-check/trends?hours=24`, { headers });
      if (res.ok) {
        const data = await res.json();
        setTrends(data);
      }
    } catch {}
  };

  useEffect(() => {
    fetchHistory();
    fetchTrends();
  }, []);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(runHealthCheck, 60000);
    return () => clearInterval(interval);
  }, [autoRefresh, runHealthCheck]);

  const maxResponseTime = latestCheck
    ? Math.max(...latestCheck.checks.map(c => c.response_time_ms), 100)
    : 100;

  return (
    <AgentLayout title="System Health">
      <div className="space-y-6" data-testid="system-health-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">System Health Monitor</h1>
            <p className="text-muted-foreground text-sm mt-1">
              Real-time monitoring of all backend services and endpoints
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant={autoRefresh ? "default" : "outline"}
              size="sm"
              onClick={() => setAutoRefresh(!autoRefresh)}
              data-testid="auto-refresh-toggle"
            >
              <Clock className="h-4 w-4 mr-1.5" />
              {autoRefresh ? "Auto: ON" : "Auto: OFF"}
            </Button>
            <Button onClick={runHealthCheck} disabled={loading} data-testid="run-health-check-btn">
              <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
              {loading ? "Checking..." : "Run Check"}
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        {latestCheck && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <StatusBadge status={latestCheck.overall_status} />
                <p className="text-xs text-muted-foreground mt-2">Overall Status</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-emerald-600">{latestCheck.summary.health_percentage}%</p>
                <p className="text-xs text-muted-foreground mt-1">Uptime</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold">{latestCheck.summary.avg_response_ms}ms</p>
                <p className="text-xs text-muted-foreground mt-1">Avg Response</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-emerald-600">{latestCheck.summary.healthy}</p>
                <p className="text-xs text-muted-foreground mt-1">Healthy</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className={`text-2xl font-bold ${latestCheck.summary.unhealthy > 0 ? "text-red-500" : "text-muted-foreground"}`}>
                  {latestCheck.summary.unhealthy}
                </p>
                <p className="text-xs text-muted-foreground mt-1">Unhealthy</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Module Status Grid */}
        {latestCheck && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Server className="h-4 w-4" />
                Module Status
                <span className="text-xs font-normal text-muted-foreground ml-auto">
                  Last check: {new Date(latestCheck.timestamp).toLocaleTimeString()}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {latestCheck.checks.map((check, i) => (
                  <div
                    key={i}
                    className={`flex items-center gap-3 p-3 rounded-lg border ${
                      check.status === "healthy" ? "border-emerald-500/20 bg-emerald-500/5" : "border-red-500/20 bg-red-500/5"
                    }`}
                    data-testid={`health-check-module-${check.name.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {check.status === "healthy" ? (
                      <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-500 shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium truncate">{check.name}</span>
                        <span className={`text-xs font-mono ${check.response_time_ms < 20 ? "text-emerald-600" : check.response_time_ms < 50 ? "text-amber-600" : "text-red-600"}`}>
                          {check.response_time_ms.toFixed(1)}ms
                        </span>
                      </div>
                      <ResponseBar value={check.response_time_ms} max={maxResponseTime} />
                      {check.error && (
                        <p className="text-xs text-red-500 mt-1 truncate">{check.error}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Module Trends */}
        {trends && Object.keys(trends.module_stats || {}).length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                24h Module Performance
                <span className="text-xs font-normal text-muted-foreground ml-auto">
                  {trends.data_points} data points
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 pr-4 font-medium">Module</th>
                      <th className="text-right py-2 px-4 font-medium">Uptime</th>
                      <th className="text-right py-2 px-4 font-medium">Avg Response</th>
                      <th className="text-right py-2 px-4 font-medium">Max Response</th>
                      <th className="text-right py-2 px-4 font-medium">Checks</th>
                      <th className="text-right py-2 pl-4 font-medium">Errors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.values(trends.module_stats)
                      .sort((a, b) => a.uptime_percent - b.uptime_percent)
                      .map((mod, i) => (
                        <tr key={i} className="border-b border-border/50 hover:bg-muted/50">
                          <td className="py-2 pr-4 font-medium">{mod.name}</td>
                          <td className="py-2 px-4 text-right">
                            <span className={`font-mono ${mod.uptime_percent === 100 ? "text-emerald-600" : mod.uptime_percent >= 90 ? "text-amber-600" : "text-red-600"}`}>
                              {mod.uptime_percent}%
                            </span>
                          </td>
                          <td className="py-2 px-4 text-right font-mono">{mod.avg_response_ms}ms</td>
                          <td className="py-2 px-4 text-right font-mono">{mod.max_response_ms}ms</td>
                          <td className="py-2 px-4 text-right">{mod.checks}</td>
                          <td className={`py-2 pl-4 text-right ${mod.error_count > 0 ? "text-red-500 font-semibold" : "text-muted-foreground"}`}>
                            {mod.error_count}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Check History Timeline */}
        {history.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Activity className="h-4 w-4" />
                Recent Health Checks
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {history.slice(0, 10).map((check, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <StatusBadge status={check.overall_status} />
                    <div className="flex-1 flex items-center gap-4">
                      <span className="text-sm">
                        <span className="font-medium">{check.summary?.healthy || 0}</span>
                        <span className="text-muted-foreground">/{check.summary?.total_checks || 0} healthy</span>
                      </span>
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Zap className="h-3 w-3" />
                        Avg: {check.summary?.avg_response_ms || 0}ms
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {new Date(check.timestamp).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Empty state */}
        {!latestCheck && history.length === 0 && (
          <Card>
            <CardContent className="p-12 text-center">
              <Database className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Health Checks Yet</h3>
              <p className="text-muted-foreground text-sm mb-6 max-w-sm mx-auto">
                Run your first health check to monitor the status of all backend modules and services.
              </p>
              <Button onClick={runHealthCheck} disabled={loading} data-testid="first-health-check-btn">
                <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
                Run First Health Check
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </AgentLayout>
  );
}
